import { useState } from "react";
import { Pencil } from "lucide-react";
import { updatePrimeContact, setPrimeContactEmailManual, type PrimeContact } from "../api/CrmApi";
import Modal from "shared/Modal";
import Button from "shared/Button";
import Alert from "shared/Alert";
import { FORM_CONTROL } from "shared/tableStyles";

interface Props {
  prime: PrimeContact;
  onClose: () => void;
  onSaved?: (prime: PrimeContact) => void;
}

const LABEL = "block text-xs font-medium text-muted-foreground mb-1.5";
const INPUT = `w-full px-3 py-2 ${FORM_CONTROL}`;

export default function EditPrimeContactModal({ prime, onClose, onSaved }: Props) {
  const [email, setEmail] = useState(prime.primary_email ?? "");
  // The address as last saved — advances after a successful email change, so a
  // retry (e.g. the other fields failed) doesn't resend an already-applied email.
  const [savedEmail, setSavedEmail] = useState(prime.primary_email ?? "");
  const [firstName, setFirstName] = useState(prime.first_name ?? "");
  const [lastName, setLastName] = useState(prime.last_name ?? "");
  const [title, setTitle] = useState(prime.title ?? "");
  const [phone, setPhone] = useState(prime.phone ?? "");
  const [personalLinkedinUrl, setPersonalLinkedinUrl] = useState(prime.personal_linkedin_url ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function confirm() {
    setSaving(true);
    setError(null);
    try {
      const newEmail = email.trim();
      if (newEmail.toLowerCase() !== savedEmail.trim().toLowerCase()) {
        // Server validates (email-shaped, not already another contact's) and
        // re-resolves the company from the new domain.
        await setPrimeContactEmailManual(prime.id, newEmail, "primary");
        setSavedEmail(newEmail);
      }
      const updated = await updatePrimeContact(prime.id, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        title: title.trim(),
        phone: phone.trim(),
        personal_linkedin_url: personalLinkedinUrl.trim(),
      });
      onSaved?.(updated);
      setSaved(true);
      setTimeout(onClose, 600);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      size="md"
      title="Edit Contact"
      description={prime.primary_email}
      icon={<Pencil />}
      onClick={saving ? undefined : onClose}
      closeButtonCLick={saving ? undefined : onClose}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={confirm} disabled={saving}>
            {saving ? "Saving…" : saved ? "Saved" : "Save"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className={LABEL}>Email</label>
          <input type="text" value={email} onChange={(e) => setEmail(e.target.value)} className={INPUT} />
          <p className="text-[11px] text-muted-foreground mt-1">
            Changing this marks the address verified and re-links the company from its domain. The previous address is kept as the secondary email (a malformed value is dropped instead).
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL}>First Name</label>
            <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={INPUT} />
          </div>
          <div>
            <label className={LABEL}>Last Name</label>
            <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className={INPUT} />
          </div>
        </div>
        <div>
          <label className={LABEL}>Title</label>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Director of Procurement" className={INPUT} />
        </div>
        <div>
          <label className={LABEL}>Phone</label>
          <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} className={INPUT} />
        </div>
        <div>
          <label className={LABEL}>Personal LinkedIn URL</label>
          <input
            type="text"
            value={personalLinkedinUrl}
            onChange={(e) => setPersonalLinkedinUrl(e.target.value)}
            placeholder="https://www.linkedin.com/in/..."
            className={INPUT}
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            This person's own profile — separate from the company LinkedIn page shown next to their company link, which comes from the primes CSV.
          </p>
        </div>
      </div>

      {error && (
        <Alert tone="danger" className="mt-4">{error}</Alert>
      )}
    </Modal>
  );
}
